import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface AdjustSourceType {
  id: number;
  name: string;
}

interface BatchDetails {
  stockId: number;
  posId: number;
  posCode: string;
  productId: number;
  productName: string;
  batchId: number;
  batchCode: string;
  stock: number;
  availableStock: number;
  expiryDate: string;
  measurementType: string;
}

interface AdjustDialogProps {
  visible: boolean;
  productId: number | null;
  posId: number | null;
  productName: string;
  posName: string;
  onHide: () => void;
  onSuccess: () => void;
  minOrderQty?: number;
}

const AdjustDialog: React.FC<AdjustDialogProps> = ({
  visible,
  productId,
  posId,
  productName,
  posName,
  onHide,
  onSuccess,
  minOrderQty,
}) => {
  const toast = useRef<Toast>(null);
  const [adjustSourceTypes, setAdjustSourceTypes] = useState<AdjustSourceType[]>([]);
  const [selectedAdjustSourceTypeId, setSelectedAdjustSourceTypeId] = useState<number | null>(null);
  const [batches, setBatches] = useState<BatchDetails[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<BatchDetails | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [reason, setReason] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const allowDecimals = minOrderQty ? minOrderQty % 1 !== 0 : false;

  const formatDateOnly = (dateString: string): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchAdjustSourceTypes = async () => {
    try {
      const response = await posService.GetAdjustSourceType();
      if (response && Array.isArray(response)) {
        setAdjustSourceTypes(response);
      }
    } catch (error) {
      console.error("Error fetching adjust source types:", error);
      showMessage("Error", "Failed to load adjust source types", "error");
    }
  };

  const fetchBatches = async () => {
    if (!productId || !posId) return;

    try {
      setLoading(true);
      const response = await posService.getBatchDetailsByProductId(productId, posId);

      if (response?.stocks && Array.isArray(response.stocks)) {
        setBatches(response.stocks);
      } else if (Array.isArray(response)) {
        setBatches(response);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      showMessage("Error", "Failed to load batches", "error");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchAdjustSourceTypes();
      fetchBatches();
      // Reset form
      setSelectedAdjustSourceTypeId(null);
      setSelectedBatch(null);
      setQuantity(null);
      setReason("");
      setNote("");
    }
  }, [visible, productId, posId]);

  const handleSave = async () => {
    // Validation
    if (!selectedAdjustSourceTypeId) {
      showMessage("Validation Error", "Please select an adjust source type", "warn");
      return;
    }

    if (!selectedBatch) {
      showMessage("Validation Error", "Please select a batch", "warn");
      return;
    }

    if (!quantity || quantity === 0) {
      showMessage("Validation Error", "Quantity cannot be zero", "warn");
      return;
    }

    if (!reason.trim()) {
      showMessage("Validation Error", "Reason is mandatory", "warn");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        posId: posId!,
        productId: productId!,
        batchId: selectedBatch.batchId,
        adjustmentQuantity: quantity!,
        adjustSourceTypeId: selectedAdjustSourceTypeId,
        reason: reason.trim(),
        note: note.trim(),
      };

      const response = await posService.adjustProductFromBatch(payload);

      if (response?.isSuccess) {
        showMessage("Success", "Stock adjusted successfully", "success");
        onSuccess();
        onHide();
      } else {
        showMessage("Error", response?.message || "Failed to adjust stock", "error");
      }
    } catch (error) {
      console.error("Error in adjust stock:", error);
      showMessage("Error", "An error occurred while processing adjustment", "error");
    } finally {
      setLoading(false);
    }
  };

  const batchOptionTemplate = (option: BatchDetails) => {
    return (
      <div className="py-2 px-1 text-sm">
        <div className="font-medium text-gray-900">{option.batchCode}</div>
        <div className="text-gray-600 text-xs mt-1">
          Stock: {option.availableStock} • Exp: {formatDateOnly(option.expiryDate)}
        </div>
      </div>
    );
  };

  const selectedBatchTemplate = (option: BatchDetails | null) => {
    if (!option) return <span className="text-gray-500">Select batch</span>;
    return (
      <div className="text-sm">
        <span className="font-medium text-gray-900">{option.batchCode}</span>
        <span className="text-gray-600 ml-2">(Stock: {option.availableStock})</span>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Adjust Stock"
        modal
        className="p-fluid"
        style={{ width: "40vw" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
      >
        <div>
          {/* POS Name and Product Name */}
          <div className="field mb-3">
            <label className="font-semibold text-sm block mb-1">Product & POS</label>
            <div className="flex gap-2">
              {/* POS (30%) */}
              <div style={{ flex: "0 0 30%" }}>
                <InputText
                  value={posName}
                  disabled
                  className="w-full"
                  style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                />
              </div>

              {/* Product Name (70%) */}
              <div style={{ flex: "1 0 70%" }}>
                <InputText
                  value={productName}
                  disabled
                  className="w-full"
                  style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                />
              </div>
            </div>
          </div>

          {/* Adjust Source Type Selection */}
          <div className="field mb-3">
            <label htmlFor="adjustSourceType" className="font-semibold text-sm block mb-1">
              Adjust Source Type <span className="text-red-500">*</span>
            </label>
            <Dropdown
              id="adjustSourceType"
              value={selectedAdjustSourceTypeId}
              options={adjustSourceTypes}
              onChange={(e) => setSelectedAdjustSourceTypeId(e.value)}
              optionLabel="name"
              optionValue="id"
              placeholder="Select adjust source type"
              className="w-full"
              disabled={adjustSourceTypes.length === 0}
              emptyMessage="No adjust source types available"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          {/* Batch Selection */}
          <div className="field mb-3">
            <label htmlFor="batch" className="font-semibold text-sm block mb-1">
              Select Batch <span className="text-red-500">*</span>
            </label>
            <Dropdown
              id="batch"
              value={selectedBatch}
              options={batches}
              onChange={(e) => setSelectedBatch(e.value)}
              optionLabel="batchCode"
              placeholder="Select a batch"
              filter
              filterBy="batchCode"
              itemTemplate={batchOptionTemplate}
              valueTemplate={selectedBatchTemplate}
              className="w-full"
              disabled={loading || batches.length === 0}
              emptyMessage="No batches available"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {selectedBatch && (
              <small className="text-gray-600 block mt-2">
                Expires: <span className="font-semibold text-gray-900">{formatDateOnly(selectedBatch.expiryDate)}</span> • 
                Stock: <span className="font-semibold text-gray-900">{selectedBatch.availableStock}</span>
              </small>
            )}
          </div>

          {/* Adjustment Quantity */}
          <div className="field mb-3">
            <label htmlFor="quantity" className="font-semibold text-sm block mb-1">
              Adjustment Quantity <span className="text-red-500">*</span>
            </label>
            <InputNumber
              id="quantity"
              value={quantity}
              onValueChange={(e) => setQuantity(e.value ?? null)}
              placeholder="Enter quantity (positive to add, negative to reduce)"
              useGrouping={false}
              className="w-full"
              disabled={!selectedBatch}
              min={0}
              minFractionDigits={0} 
              maxFractionDigits={allowDecimals ? 3 : 0} 
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {/* <small className="text-gray-600 block mt-1">
              Use positive values to increase stock, negative values to decrease stock
            </small> */}
          </div>

          {/* Reason */}
          <div className="field mb-3">
            <label htmlFor="reason" className="font-semibold text-sm block mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <InputText
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for adjustment"
              className="w-full"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          {/* Note */}
          <div className="field mb-3">
            <label htmlFor="note" className="font-semibold text-sm block mb-1">
              Note
            </label>
            <InputText
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter additional notes (optional)"
              className="w-full"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              onClick={onHide}
              severity="secondary"
              className="text-black border-round p-button-m"
              disabled={loading}
            />
            <Button
              label="Save"
              onClick={handleSave}
              loading={loading}
              // disabled={loading || !selectedBatch || quantity === 0 || !selectedAdjustSourceTypeId || !reason.trim()}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AdjustDialog;
